import React, { useState } from 'react';
import AppIcon from '../common/AppIcon';
import GradientIcon from '../common/GradientIcon';
import EquipmentLoadout from '../common/EquipmentLoadout';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import { useShopItems } from '../../hooks/queries';

const ShopPage = () => {
  const { user, reloadUser } = useAuth();
  const [selectedCategory, setSelectedCategory] = useState('all');

  // 🟢 [P2] 商品数据改用 React Query：切回商店命中缓存、无闪烁（已在 hook 内按积分升序）
  const { data: shopItems = [], isLoading: loading } = useShopItems();
  const [purchasingId, setPurchasingId] = useState(null);

  const categories = [
    { id: 'all', name: 'All Items', icon: '🛒' },
    { id: 'headgear', name: 'Headgear', icon: '🧢' },
    { id: 'eyewear', name: 'Eyewear', icon: '😎' },
    { id: 'accessory', name: 'Accessories', icon: '🧣' }
  ];

  const filteredItems = selectedCategory === 'all' 
    ? shopItems 
    : shopItems.filter(item => item.type === selectedCategory);

  // 2. 处理购买逻辑
  const handlePurchase = async (item) => {
    // 确保 user.points 是数字，防止 undefined
    const currentPoints = user?.points || 0;

    if (currentPoints < item.price) {
      alert('Not enough points!'); // 或者使用更优雅的 UI 提示
      return;
    }

    try {
      setPurchasingId(item.id); 
      
      await apiService.purchaseItem(item.id);
      await reloadUser(); // 刷新余额
      
      console.log(`Successfully bought ${item.name}`);
      
    } catch (error) {
      console.error("Purchase failed", error);
      alert(error.response?.data?.error || 'Purchase failed. Please try again.');
    } finally {
      setPurchasingId(null);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-sky-100">
      <div className="text-2xl font-bold text-gray-500 flex items-center gap-2">
        <AppIcon name="spinner" /> Loading Shop...
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ y: -50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8 }} className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2">🛒 Vocab Shop</h1>
          <p className="text-xl text-gray-700">Spend your points on awesome gear!</p>
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5, delay: 0.2 }} className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 mt-6 inline-block">
            <div className="text-4xl font-bold text-gray-800">
              <AppIcon name="coins" className="mr-2 text-yellow-400" />{user?.points || 0} Points
            </div>
            <div className="text-sm text-gray-700">Available to spend</div>
          </motion.div>
        </motion.div>

        {/* 🟢 [方案B] 我的装备架 */}
        <div className="max-w-3xl mx-auto bg-white bg-opacity-30 backdrop-blur-lg rounded-2xl p-4 mb-8">
          <div className="text-xs text-gray-600 font-bold uppercase mb-2 text-center">My Gear</div>
          <EquipmentLoadout inventory={user?.inventory || []} className="justify-center" />
        </div>

        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.4 }} className="flex flex-wrap justify-center gap-4 mb-8">
          {categories.map((category) => (
            <motion.button key={category.id} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setSelectedCategory(category.id)} className={`px-6 py-3 rounded-full font-bold shadow-lg transition-all duration-200 ${selectedCategory === category.id ? 'bg-white text-purple-600 ring-4 ring-white ring-opacity-50' : 'bg-white bg-opacity-20 text-gray-800 hover:bg-opacity-30'}`}>
              <span className="mr-2">{category.icon}</span>{category.name}
            </motion.button>
          ))}
        </motion.div>

        <motion.div initial={{ y: 50, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.8, delay: 0.6 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {filteredItems.map((item, index) => {
            const isPurchased = user?.inventory?.includes(item.id);
            const canAfford = (user?.points || 0) >= item.price;
            const isProcessing = purchasingId === item.id;

            return (
              <motion.div key={item.id} initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.5, delay: index * 0.1 }} whileHover={{ scale: 1.05, y: -5 }} className={`bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-6 text-gray-800 shadow-xl hover:shadow-2xl transition-all duration-300 ${isPurchased ? 'ring-4 ring-green-400' : ''}`}>
                <div className="text-center mb-4">
                  <div className="text-6xl mb-2">{item.icon}</div>
                  <h3 className="text-xl font-bold">{item.name}</h3>
                  <p className="text-sm text-gray-700 capitalize">{item.type}</p>
                </div>
                <div className="text-center mb-4">
                    <p className="text-sm text-gray-600 mb-2 h-10 flex items-center justify-center">{item.description}</p>
                    <div className="text-2xl font-bold text-yellow-400"><AppIcon name="coins" className="mr-1" />{item.price}</div>
                </div>
                
                <motion.button 
                    whileHover={{ scale: canAfford && !isPurchased ? 1.02 : 1 }} 
                    whileTap={{ scale: canAfford && !isPurchased ? 0.98 : 1 }} 
                    onClick={() => !isPurchased && canAfford && handlePurchase(item)} 
                    disabled={isPurchased || !canAfford || isProcessing} 
                    className={`w-full font-bold py-3 px-6 rounded-lg transition-all duration-200 flex justify-center items-center ${isPurchased ? 'bg-green-500 text-white cursor-default' : canAfford ? 'bg-white text-purple-600 hover:bg-gray-100' : 'bg-gray-500 text-gray-300 cursor-not-allowed'}`}
                >
                  {isProcessing ? (
                      <><AppIcon name="spinner" className="mr-2" /> Buying...</>
                  ) : isPurchased ? (
                      <><AppIcon name="check" className="mr-2" /> Owned</>
                  ) : canAfford ? (
                      'Buy Now'
                  ) : (
                      'Not Enough Points'
                  )}
                </motion.button>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </div>
  );
};

export default ShopPage;